import { Lightbulb, Sparkles } from 'lucide-react'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import {
  isInsightScope,
  useStreamingInsights,
  type StreamingInsightsState,
} from './use-streaming-insights'
import { useTypewriter } from '@/hooks/useTypewriter'

/** Page calls the hook once and passes the same state to both components,
 *  so we open exactly one stream per (platforms, timezone) change. */
export function useAnalyticsInsights(platforms: PlatformType[]) {
  return useStreamingInsights(platforms)
}

export function AIInsightsHeader({
  platforms,
  state,
}: {
  platforms: PlatformType[]
  state: StreamingInsightsState
}) {
  // Skip the typewriter animation entirely when serving from cache —
  // re-visiting the page should feel like recall, not regeneration.
  const animate = state.cached === false
  const displayedInsight = useTypewriter(state.insight, { enabled: animate })
  const isTyping = animate && (displayedInsight.length < state.insight.length || state.isLoading)

  if (!isInsightScope(platforms)) return null

  // While we don't yet know if there's enough data, render the skeleton —
  // first chunk arrives within ~100ms, so the empty/loading flicker is brief.
  if (state.hasEnoughData === null && state.isLoading) {
    return <Skeleton variant="header" />
  }

  if (!state.hasEnoughData) return null
  if (!state.insight && state.isLoading) return <Skeleton variant="header" />

  return (
    <div className="mb-4 flex items-start gap-3 rounded-lg border border-purple-200 bg-purple-50/50 px-4 py-3 dark:border-purple-900/40 dark:bg-purple-950/20">
      <Sparkles
        size={16}
        className="mt-0.5 shrink-0 text-purple-600 dark:text-purple-400"
        aria-hidden
      />
      <div className="flex-1">
        <h3 className="text-foreground text-sm font-semibold">What's Working For You</h3>
        <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
          {displayedInsight}
          {isTyping && <Caret />}
        </p>
      </div>
    </div>
  )
}

export function AIInsightsSuggestion({
  platforms,
  state,
}: {
  platforms: PlatformType[]
  state: StreamingInsightsState
}) {
  const animate = state.cached === false
  const displayedSuggestion = useTypewriter(state.suggestion, { enabled: animate })
  const isTyping =
    animate && (displayedSuggestion.length < state.suggestion.length || state.isLoading)

  if (!isInsightScope(platforms)) return null

  if (state.hasEnoughData === null && state.isLoading) {
    return <Skeleton variant="suggestion" />
  }
  if (!state.hasEnoughData) return null
  // Hide the suggestion until it actually starts streaming. The header card
  // already conveys "we're working on it" via the insight that's animating in.
  if (!state.suggestion) return null

  return (
    <div className="border-border/60 bg-background flex items-start gap-3 rounded-lg border px-4 py-3">
      <Lightbulb size={16} className="text-foreground/70 mt-0.5 shrink-0" aria-hidden />
      <p className="text-foreground text-sm leading-relaxed">
        {displayedSuggestion}
        {isTyping && <Caret />}
      </p>
    </div>
  )
}

/** Soft blinking caret while text is still being revealed. ~600ms blink so
 *  it's noticeable but not distracting. Inline-block keeps it on the same
 *  baseline as the surrounding text. */
function Caret() {
  return (
    <span
      aria-hidden
      className="bg-muted-foreground/60 ml-0.5 inline-block h-3 w-[1.5px] animate-pulse align-middle"
    />
  )
}

function Skeleton({ variant }: { variant: 'header' | 'suggestion' }) {
  if (variant === 'header') {
    return (
      <div className="mb-4 flex items-start gap-3 rounded-lg border border-purple-200 bg-purple-50/50 px-4 py-3 dark:border-purple-900/40 dark:bg-purple-950/20">
        <Sparkles
          size={16}
          className="mt-0.5 shrink-0 text-purple-600 dark:text-purple-400"
          aria-hidden
        />
        <div className="flex-1 space-y-1.5">
          <div className="bg-muted h-3 w-40 animate-pulse rounded" />
          <div className="bg-muted h-3 w-full animate-pulse rounded" />
          <div className="bg-muted h-3 w-4/5 animate-pulse rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="border-border/60 bg-background flex items-start gap-3 rounded-lg border px-4 py-3">
      <Lightbulb size={16} className="text-foreground/60 mt-0.5 shrink-0" aria-hidden />
      <div className="flex-1 space-y-1.5">
        <div className="bg-muted h-3 w-full animate-pulse rounded" />
        <div className="bg-muted h-3 w-3/5 animate-pulse rounded" />
      </div>
    </div>
  )
}
